import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "@/config/authConfig";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { authApi } from "@/api/auth";
import { useAppDispatch } from "@/store/hooks";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { NetworkError } from "@/api/network";
import { TOTPVerifyModal } from "@/components/TOTPVerifyModal";
import { handlePostLogin } from "@/utils/postLoginHandler";
import type { LoginApiResponse } from "@/types/auth";

const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#f25022" d="M1 1h10v10H1z" />
    <path fill="#00a4ef" d="M13 1h10v10H13z" />
    <path fill="#7fba00" d="M1 13h10v10H1z" />
    <path fill="#ffb900" d="M13 13h10v10H13z" />
  </svg>
);

const decodeJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

interface PendingLogin {
  type: 'google' | 'microsoft';
  credential: string;
  userInfo: { email: string; name: string };
}

interface SocialLoginButtonsProps {
  redirect?: string;
  redirectOrigin?: string;
  service?: string;
}

export const SocialLoginButtons = ({ redirect, redirectOrigin, service }: SocialLoginButtonsProps) => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const dispatch = useAppDispatch();

  const [showTOTPModal, setShowTOTPModal] = useState(false);
  const [pendingLogin, setPendingLogin] = useState<PendingLogin | null>(null);
  const [totpError, setTotpError] = useState<string | null>(null);

  const handleGoogleSuccess = async (credential: string, totpCode?: string) => {
    try {
      const userInfo = decodeJwt(credential);
      if (!userInfo) {
        toast.error("Failed to decode Google credentials");
        return;
      }

      const response = await authApi.googleLogin(credential, redirect || redirectOrigin, totpCode, service);

      if (response.data?.totpRequired || response.data?.requiresTotp) {
        setPendingLogin({
          type: 'google',
          credential,
          userInfo: {
            email: response.data.email || userInfo.email,
            name: response.data.name || userInfo.name || userInfo.email
          }
        });
        setShowTOTPModal(true);
        setTotpError(null);
        return;
      }

      if ((response.code === 200 || response.success) && response.data?.token) {
        const userName = response.data?.name || userInfo.name || userInfo.email;
        await handlePostLogin({
          token: response.data.token,
          response,
          data: response.data,
          redirect,
          redirectOrigin,
          dispatch,
          navigate,
          userName,
        });
        return;
      }

      toast.error(response.msg || response.message || "Google login failed");
    } catch (error) {
      if (error instanceof NetworkError) {
        const errorMsg = error.message.toLowerCase();
        if (error.status === 401 && (errorMsg.includes('totp') || errorMsg.includes('authenticator'))) {
          setTotpError("Invalid authenticator code. Please try again.");
          return;
        }
        if (error.message.toLowerCase().includes('too many')) {
          toast.error("Too many attempts. Please wait 15 minutes.");
          setShowTOTPModal(false);
          setPendingLogin(null);
          setTotpError(null);
          return;
        }
        toast.error(error.message);
      } else {
        toast.error("Google login failed. Please try again.");
      }
    }
  };

  const { buttonRef } = useGoogleAuth({
    onSuccess: (credential) => handleGoogleSuccess(credential),
    onError: (error) => toast.error(error || "Google login failed"),
    enableOneTap: true,
    autoSelect: false,
  });

  const handleMicrosoftLogin = async (totpCode?: string) => {
    try {
      let accessToken: string;
      let accountInfo: { username: string; name?: string };

      if (totpCode && pendingLogin?.type === 'microsoft') {
        accessToken = pendingLogin.credential;
        accountInfo = { username: pendingLogin.userInfo.email, name: pendingLogin.userInfo.name };
      } else {
        const msalResponse = await instance.loginPopup(loginRequest);
        accessToken = msalResponse.accessToken;
        accountInfo = { username: msalResponse.account.username, name: msalResponse.account.name };
      }

      const response = await authApi.microsoftLogin(accessToken, redirect || redirectOrigin, totpCode, service);

      if (response.data?.totpRequired || response.data?.requiresTotp) {
        setPendingLogin({
          type: 'microsoft',
          credential: accessToken,
          userInfo: {
            email: response.data.email || accountInfo.username,
            name: response.data.name || accountInfo.name || accountInfo.username
          }
        });
        setShowTOTPModal(true);
        setTotpError(null);
        return;
      }

      if ((response.code === 200 || response.success) && response.data?.token) {
        const userName = response.data?.name || accountInfo.name || accountInfo.username;
        await handlePostLogin({
          token: response.data.token,
          response,
          data: response.data,
          redirect,
          redirectOrigin,
          dispatch,
          navigate,
          userName,
        });
        return;
      }

      toast.error(response.msg || response.message || "Microsoft login failed");
    } catch (error) {
      if (error instanceof NetworkError) {
        const errorMsg = error.message.toLowerCase();
        if (error.status === 401 && (errorMsg.includes('totp') || errorMsg.includes('authenticator'))) {
          setTotpError("Invalid authenticator code. Please try again.");
          return;
        }
        if (error.message.toLowerCase().includes('too many')) {
          toast.error("Too many attempts. Please wait 15 minutes.");
          setShowTOTPModal(false);
          setPendingLogin(null);
          setTotpError(null);
          return;
        }
        toast.error(error.message);
      } else {
        toast.error("Microsoft login failed. Please try again.");
      }
    }
  };

  const handleTOTPVerify = async (code: string) => {
    setTotpError(null);
    if (!pendingLogin) {
      toast.error("Session expired. Please try logging in again.");
      setShowTOTPModal(false);
      return;
    }
    if (pendingLogin.type === 'google') {
      await handleGoogleSuccess(pendingLogin.credential, code);
    } else {
      await handleMicrosoftLogin(code);
    }
  };

  const handleTOTPCancel = () => {
    setShowTOTPModal(false);
    setPendingLogin(null);
    setTotpError(null);
  };

  return (
    <>
      <div className="space-y-3">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div className="w-full flex justify-center">
            <div ref={buttonRef} className="w-full max-w-[384px] min-h-[40px]" style={{ minHeight: '40px', width: '100%' }} />
          </div>

          {/* Microsoft login button - commented out from UI */}
          {/* <Button type="button" variant="outline" onClick={() => handleMicrosoftLogin()} className="w-full">
            <MicrosoftIcon />
            Continue with Microsoft
          </Button> */}
        </div>
      </div>

      <TOTPVerifyModal
        isOpen={showTOTPModal}
        email={pendingLogin?.userInfo.email || ""}
        name={pendingLogin?.userInfo.name || ""}
        onVerify={handleTOTPVerify}
        onCancel={handleTOTPCancel}
        error={totpError}
        onErrorClear={() => setTotpError(null)}
      />
    </>
  );
};
